import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Typography from '../components/Text/Typography';
import TextInput from '../components/Text/TextInput';
import Button from '../components/Button/Button';
import Chip from '../components/Chip/Chip';
import ResponsiveDialog from '../components/ResponsiveDialog/ResponsiveDialog';
import './Onboarding.css';
import { API_BASE_URL, authenticatedFetch } from '../config/api';
// import ProductSearchDialog from '../components/ProductSearchDialog/ProductSearchDialog';

const CONCERNS = [
  "Breakouts & Blemishes",
  "Tone & Pigmentation",
  "Texture & Smoothness",
  "Moisture & Hydration",
  "Oil Control",
  "Aging & Elasticity",
  "Sensitivity & Irritation"
];

const CONDITIONS = [
  "Acne",
  "Eczema",
  "Rosacea",
  "Psoriasis",
  "Hyperpigmentation / Melasma",
  "Sensitive / Reactive Skin",
  "Fungal Acne (Malassezia Folliculitis)",
  "Keratosis Pilaris",
  "Other"
];

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [selectedConcerns, setSelectedConcerns] = useState<string[]>([]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  
  // Check if we're in edit mode
  const isEditMode = searchParams.get('edit') === 'true';


  // Load existing user data when in edit mode
  useEffect(() => {
    if (isEditMode && user) {
      // Use existing user data from auth context
      setName(user.name || '');
      
      // Convert birthday from YYYY-MM-DD to MM/DD/YYYY format for display
      let formattedDob = '';
      if (user.birthday) {
        console.log('🔍 Raw birthday from user object:', user.birthday, 'Type:', typeof user.birthday);
        try {
          // Handle different possible formats
          let dateStr = user.birthday;
          
          // If it's a Date object, convert to ISO string first
          if (user.birthday instanceof Date) {
            dateStr = user.birthday.toISOString().split('T')[0];
          }
          // If it's an ISO string, extract just the date part
          else if (typeof user.birthday === 'string' && user.birthday.includes('T')) {
            dateStr = user.birthday.split('T')[0];
          }
          
          console.log('🔍 Processed date string:', dateStr);
          const [year, month, day] = dateStr.split('-');
          if (year && month && day) {
            formattedDob = `${month}/${day}/${year}`;
            console.log('🔍 Final formatted DOB:', formattedDob);
          }
        } catch (error) {
          console.error('Error parsing birthday:', user.birthday, error);
        }
      }
      setDob(formattedDob);
      
      // Set concerns and conditions
      setSelectedConcerns(user.skin_concerns || []);
      setSelectedConditions(user.skin_conditions || []);
    }
  }, [isEditMode, user]);

  const handleNextStep1 = (e?: React.FormEvent) => {
    e?.preventDefault();
    setStep(2);
  };

  const handleNextStep2 = () => setStep(3);
  const handleBackStep2 = () => setStep(1);

  const handleNextStep3 = () => {
    handleFinish();
  };
  const handleBackStep3 = () => setStep(2);

  const toggleConcern = (concern: string) => {
    setSelectedConcerns(sel =>
      sel.includes(concern) ? sel.filter(c => c !== concern) : [...sel, concern]
    );
  };

  const toggleCondition = (condition: string) => {
    setSelectedConditions(sel =>
      sel.includes(condition) ? sel.filter(c => c !== condition) : [...sel, condition]
    );
  };

  const handleFinish = async () => {
    try {
      if (!user || !user.id) {
        console.error('User or user.id not available');
        return;
      }

      // Save onboarding data to server
      const response = await authenticatedFetch(`${API_BASE_URL}/api/user/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          onboardingCompleted: true,
          name,
          dob,
          skinConcerns: selectedConcerns,
          skinConditions: selectedConditions
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save onboarding data');
      }

      // Onboarding completed successfully
      console.log('Onboarding completed successfully');

      // Refresh user data to get updated onboarding_completed status
      await refreshUser();

      // Navigate to homepage (or back to previous page if in edit mode)
      if (isEditMode) {
        navigate(-1); // Go back to previous page
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error saving onboarding data:', error);
      // You might want to show an error message to the user here
    }
  };




  return (
    <div className="container">
      {step === 1 && (
        <>
          <div className="stepHeader">
            <Typography variant="body-small-bold" className="stepLabel">STEP 1</Typography>
            <Typography variant="h1" className="stepTitle">
              {isEditMode ? 'Update your profile information' : "Let's start with introductions..."}
            </Typography>
          </div>
          <form onSubmit={handleNextStep1} className="form">
            <div className="inputGroup">
              <Typography variant="body" className="inputLabel">Name</Typography>
              <TextInput
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Enter your preferred name"
              />
            </div>
            <div className="inputGroup">
              <Typography variant="body" className="inputLabel">Date of Birth</Typography>
              <TextInput
                value={dob}
                onChange={e => setDob(e.target.value)}
                placeholder="MM/DD/YYYY"
              />
            </div>
            <div className="buttonContainer">
              <Button variant="primary" className="primaryButton" onClick={handleNextStep1}>
                {isEditMode ? 'Next' : 'Next'}
              </Button>
            </div>
          </form>
        </>
      )}
      {step === 2 && (
        <div className="container">
          <div className="stepHeader">
            <Typography variant="body" className="stepLabel">STEP 2</Typography>
            <Typography variant="h1" className="stepTitle">
              {isEditMode ? 'Update your skin concerns' : 'Select your main concerns...'}
            </Typography>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'start', marginBottom: 40 }}>
            {CONCERNS.map(concern => (
              <Chip
                key={concern}
                label={concern}
                isSelected={selectedConcerns.includes(concern)}
                onClick={() => toggleConcern(concern)}
              />
            ))}
          </div>
          <div className="buttonContainer">
            <Button variant="primary" className="primaryButton" onClick={handleNextStep2}>Next</Button>
            <Button variant="text" className="backButton" onClick={handleBackStep2}>Go back</Button>
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="container">
          <div className="stepHeader">
            <Typography variant="body" className="stepLabel">STEP 3</Typography>
            <Typography variant="h1" className="stepTitle">
              {isEditMode ? 'Update your skin conditions' : 'Do you have any pre-existing skin conditions?'}
            </Typography>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'start', marginBottom: 40 }}>
            {CONDITIONS.map(condition => (
              <Chip
                key={condition}
                label={condition}
                isSelected={selectedConditions.includes(condition)}
                onClick={() => toggleCondition(condition)}
              />
            ))}
          </div>
          <div className="terms-privacy-text" style={{ marginBottom: 24, textAlign: 'center' }}>
            <Typography variant="body-small" style={{ color: 'var(--color-gray)', lineHeight: 1.4 }}>
              By signing up, you agree to Dew's{' '}
              <button
                type="button"
                onClick={() => setShowTermsDialog(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  padding: 0
                }}
              >
                Terms & Conditions
              </button>
              {' '}and{' '}
              <button
                type="button"
                onClick={() => setShowPrivacyDialog(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  padding: 0
                }}
              >
                Privacy Policy
              </button>
              .
            </Typography>
          </div>
          <div className="buttonContainer">
            <Button variant="primary" className="primaryButton" onClick={handleNextStep3}>
              {isEditMode ? 'Save Changes' : 'Finish'}
            </Button>
            <Button variant="text" className="backButton" onClick={handleBackStep3}>Go back</Button>
          </div>
        </div>
      )}


      {/* Terms & Conditions Dialog */}
      <ResponsiveDialog
        type="terms"
        isOpen={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
      />

      {/* Privacy Policy Dialog */}
      <ResponsiveDialog
        type="privacy"
        isOpen={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
      />
    </div>
  );
};

export default Onboarding; 